import { Injectable, inject } from '@angular/core';
import {
  infiniteQueryOptions,
  injectQueryClient,
  mutationOptions,
  queryOptions,
} from '@tanstack/angular-query-experimental';
import { BackendService } from '../services/backend.service';
import {
  BackendRequestParams,
  Transaction,
  TransactionPage,
  TransactionParams,
  TransactionFilters,
  TransactionFilterParams,
  UpdateMultipleTransactionsPayload,
  UpdateTransactionPayload,
  Bucket,
} from '../types';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';
import { UserService } from './user.service';
import { TransactionExportService } from './transaction-export.service';
import { GroupService } from './group.service';
import { BucketService } from './bucket.service';
import {
  CreateTransactionRequest,
  PatchTransactionsRequest,
  Transaction as ApiTransaction,
  TransactionPage as ApiTransactionPage,
  Transactions,
  UpdateTransactionRequest,
} from '../generated-api/models';
import { transactionQueryKeys, userQueryKeys } from './query-keys';

export const PAGE_SIZE: number = 100;
type UpdateMultipleTransactionsResponse = Transactions & { data: Transaction[] };
export type UpdateMultipleTransactionsMutationVariables = {
  data: UpdateMultipleTransactionsPayload;
  previousTransactions?: Array<Pick<Transaction, 'bucketId' | 'date'>>;
};
export type DeleteTransactionMutationVariables = {
  transactionId: string;
  monthYear?: string;
};
export type DeleteTransferMutationVariables = {
  transferId: string;
  monthYear?: string;
};
type TransactionMutationCacheImpact = {
  bucketDetails?: Array<Pick<Transaction, 'bucketId' | 'date'> | Pick<UpdateTransactionPayload, 'bucketId' | 'date'>>;
  monthYears?: Array<string | null | undefined>;
};
const UNBUDGETED_TRANSACTIONS_FILTER: TransactionFilters = {
  labelIds: [],
  bucketIds: [],
  deletedOnly: false,
  unbucketedOnly: true,
};
const UNBUDGETED_TRANSACTIONS_WITH_SUGGESTIONS_FILTER: TransactionFilters = {
  ...UNBUDGETED_TRANSACTIONS_FILTER,
  suggestedBucketIds: true,
};
const DELETED_TRANSACTIONS_FILTER: TransactionFilters = {
  labelIds: [],
  bucketIds: [],
  deletedOnly: true,
  unbucketedOnly: false,
};
@Injectable({ providedIn: 'root' })
export class TransactionService {
  private backend = inject(BackendService);
  userService = inject(UserService);
  private transactionExport = inject(TransactionExportService);
  private groupService = inject(GroupService);
  private bucketService = inject(BucketService);

  private readonly queryClient = injectQueryClient();
  private readonly inFlightTransactionRequests = new Map<string, Promise<TransactionPage>>();
  private readonly transactionPageFilters = new Map<string, TransactionFilters>();

  downloadTransactions$ = (filter: TransactionFilters): Observable<Blob> =>
    this.transactionExport.downloadTransactions$({
      type: 'find',
      apiRoute: 'transaction/export',
      params: {
        ...this.createFilterParams(filter),
      },
    });

  cachedTransactions: (filter?: TransactionFilters) => TransactionPage = filter => {
    if (filter) {
      if (this.isUnbudgetedTransactionsWithSuggestionsFilter(filter))
        return this.cachedUnbudgetedTransactionsWithSuggestions();
      if (this.isUnbudgetedTransactionsFilter(filter)) return this.cachedUnbudgetedTransactions();
      if (this.isDeletedTransactionsFilter(filter)) return this.cachedDeletedTransactions();

      const filterKey = this.transactionPageKey(filter);
      const cachedQueryPage = this.queryClient.getQueryData<TransactionPage>(transactionQueryKeys.page(filterKey));
      if (cachedQueryPage) return cachedQueryPage;
      return {} as TransactionPage;
    }

    const cachedQueryPage = this.queryClient.getQueryData<TransactionPage>(
      transactionQueryKeys.page(this.transactionPageKey(undefined)),
    );
    return cachedQueryPage || ({} as TransactionPage);
  };

  setTransactions(transactionPage: TransactionPage) {
    const filter = this.filterFromPage(transactionPage);
    const key = this.transactionPageKey(filter);
    this.rememberTransactionPageFilter(key, filter);
    this.queryClient.setQueryData(transactionQueryKeys.page(key), transactionPage);
  }

  setUnbudgetedTransactions(transactionPage: TransactionPage) {
    const key = this.unbudgetedTransactionsKey();
    this.rememberTransactionPageFilter(key, UNBUDGETED_TRANSACTIONS_FILTER);
    this.queryClient.setQueryData(transactionQueryKeys.page(key), transactionPage);
  }

  setUnbudgetedTransactionsWithSuggestions(transactionPage: TransactionPage) {
    const key = this.unbudgetedTransactionsWithSuggestionsKey();
    this.rememberTransactionPageFilter(key, UNBUDGETED_TRANSACTIONS_WITH_SUGGESTIONS_FILTER);
    this.queryClient.setQueryData(transactionQueryKeys.page(key), transactionPage);
  }

  setDeletedransactions(transactionPage: TransactionPage) {
    const key = this.deletedTransactionsKey();
    this.rememberTransactionPageFilter(key, DELETED_TRANSACTIONS_FILTER);
    this.queryClient.setQueryData(transactionQueryKeys.page(key), transactionPage);
  }

  editingInTransactionList$: BehaviorSubject<null | string> = new BehaviorSubject<null | string>(null);

  fetchTransactions(filter: TransactionFilters): Promise<TransactionPage> {
    const key = this.transactionPageKey(filter);
    return this.requestTransactionPage(key, filter, {
      pageSize: PAGE_SIZE,
      ...this.createFilterParams(filter),
    });
  }

  bucketTransactionsQueryOptions(bucketId: string, monthYear: string, pageNumber = 1, pageSize = PAGE_SIZE) {
    return queryOptions({
      queryKey: transactionQueryKeys.bucketPage(bucketId, monthYear, pageNumber, pageSize),
      queryFn: () => this.fetchBucketTransactionPageFromBackend(bucketId, monthYear, pageNumber, pageSize),
    });
  }

  transactionInfiniteQueryOptions(filter: TransactionFilters) {
    const filterKey = this.transactionPageKey(filter);
    const cachedPage = this.cachedTransactions(filter);
    this.rememberTransactionPageFilter(filterKey, filter);

    return infiniteQueryOptions({
      queryKey: transactionQueryKeys.infiniteList(filterKey),
      queryFn: ({ pageParam }) =>
        this.fetchTransactionPageFromBackend({
          pageSize: PAGE_SIZE,
          pageNumber: pageParam,
          ...this.createFilterParams(filter),
        }).then(transactionPage => {
          this.persistTransactionPage(filter, transactionPage);
          return transactionPage;
        }),
      initialPageParam: 1,
      getNextPageParam: lastPage =>
        lastPage.data.length > 0 && lastPage.totalRecords > lastPage.pageNumber * lastPage.pageSize
          ? lastPage.pageNumber + 1
          : undefined,
      ...(cachedPage.data ? { initialData: { pages: [cachedPage], pageParams: [cachedPage.pageNumber ?? 1] } } : {}),
    });
  }

  mergeTransactionPages(pages: TransactionPage[], fallbackFilter?: TransactionFilters): TransactionPage | null {
    const lastPage = pages.at(-1);
    if (!lastPage) return null;

    const params = fallbackFilter
      ? this.transactionPageParamsFromFilter(fallbackFilter, lastPage.pageNumber, lastPage.pageSize)
      : lastPage.params;

    return {
      ...lastPage,
      data: pages.flatMap(page => page.data),
      params,
    };
  }

  createTransactionMutationOptions() {
    return mutationOptions({
      mutationKey: ['transaction', 'create'],
      mutationFn: (data: Partial<Transaction>) => this.createTransaction(data),
      onSuccess: (transaction, data) => {
        this.invalidateAfterTransactionMutation({
          monthYears: [
            this.monthYearFromTransactionDate(data.date),
            this.monthYearFromTransactionDate(transaction.date),
          ],
        });
      },
    });
  }

  updateTransactionMutationOptions() {
    return mutationOptions({
      mutationKey: ['transaction', 'update'],
      mutationFn: (data: UpdateTransactionPayload) => this.updateTransactionRequest(data),
      onSuccess: (transaction, data) => {
        const bucketDetails = [data, transaction, ...(data.children || []), ...(transaction.children || [])];
        this.invalidateAfterTransactionMutation({
          bucketDetails,
          monthYears: [
            this.monthYearFromTransactionDate(data.date),
            this.monthYearFromTransactionDate(transaction.date),
            ...(transaction.children || []).map(child => this.monthYearFromTransactionDate(child.date)),
          ],
        });
      },
    });
  }

  updateMultipleTransactionsMutationOptions() {
    return mutationOptions({
      mutationKey: ['transactions', 'update'],
      mutationFn: ({ data }: UpdateMultipleTransactionsMutationVariables) =>
        this.updateMultipleTransactionsRequest(data),
      onSuccess: (response, variables) =>
        this.invalidateAfterBulkMutation([...(variables.previousTransactions || []), ...(response.data || [])]),
    });
  }

  deleteTransactionMutationOptions() {
    return mutationOptions({
      mutationKey: ['transaction', 'delete'],
      mutationFn: ({ transactionId }: DeleteTransactionMutationVariables) =>
        this.deleteTransactionRequest(transactionId),
      onSuccess: (_response, variables) =>
        this.invalidateAfterTransactionMutation({ monthYears: [variables.monthYear] }),
    });
  }

  deleteTransferMutationOptions() {
    return mutationOptions({
      mutationKey: ['fund-transfer', 'delete'],
      mutationFn: ({ transferId }: DeleteTransferMutationVariables) => this.deleteTransferRequest(transferId),
      onSuccess: (_response, variables) =>
        this.invalidateAfterTransactionMutation({ monthYears: [variables.monthYear] }),
    });
  }

  undeleteTransactionMutationOptions() {
    return mutationOptions({
      mutationKey: ['transaction', 'undelete'],
      mutationFn: ({ transactionId }: DeleteTransactionMutationVariables) =>
        this.undeleteTransactionRequest(transactionId),
      onSuccess: (_response, variables) =>
        this.invalidateAfterTransactionMutation({ monthYears: [variables.monthYear] }),
    });
  }

  createTransaction(data: Partial<Transaction>): Promise<Transaction> {
    return firstValueFrom(
      this.backend.request<Transaction & ApiTransaction, Partial<CreateTransactionRequest>>({
        type: 'post',
        apiRoute: 'transaction',
        data: this.buildCreateTransactionRequest(data),
      }),
    );
  }

  transactionDetailQueryOptions(transactionId: string) {
    return queryOptions({
      queryKey: transactionQueryKeys.detail(transactionId),
      queryFn: () => this.fetchTransactionRequest(transactionId),
      staleTime: 0,
    });
  }

  fetchTransactionRequest(transactionId: string): Promise<Transaction> {
    return firstValueFrom(this.backend.request<Transaction>({ type: 'get', apiRoute: `transaction/${transactionId}` }));
  }

  updateTransactionRequest(data: UpdateTransactionPayload): Promise<Transaction> {
    return firstValueFrom(
      this.backend.request<Transaction & ApiTransaction, UpdateTransactionRequest>({
        type: 'update',
        apiRoute: `transaction/${data.id}`,
        data: this.buildUpdateTransactionRequest(data),
      }),
    );
  }

  updateMultipleTransactionsRequest(
    data: UpdateMultipleTransactionsPayload,
  ): Promise<UpdateMultipleTransactionsResponse> {
    return firstValueFrom(
      this.backend.request<UpdateMultipleTransactionsResponse, PatchTransactionsRequest>({
        type: 'patch',
        apiRoute: 'transactions',
        data,
      }),
    );
  }

  deleteTransactionRequest(transactionId: string): Promise<void> {
    return firstValueFrom(
      this.backend.request<void>({
        type: 'delete',
        apiRoute: `transaction/${transactionId}`,
      }),
    );
  }

  deleteTransferRequest(transferId: string): Promise<void> {
    return firstValueFrom(this.backend.request<void>({ type: 'delete', apiRoute: `fundTransfer/${transferId}` }));
  }

  undeleteTransactionRequest(transactionId: string): Promise<void> {
    return firstValueFrom(
      this.backend.request<void>({
        type: 'delete',
        apiRoute: `transaction/${transactionId}`,
        params: { delete: 'false' },
      }),
    );
  }

  private buildCreateTransactionRequest(data: Partial<Transaction>): Partial<CreateTransactionRequest> {
    return {
      amount: data.amount,
      merchant: data.merchant,
      description: data.description,
      labelIds: data.labelIds,
      date: data.date,
      bucketId: data.bucketId,
      type: data.type as CreateTransactionRequest['type'] | undefined,
      children: data.children?.map(child => ({
        id: child.id,
        amount: child.amount,
        description: child.description,
        labelIds: child.labelIds,
        bucketId: child.bucketId,
      })),
    };
  }

  private buildUpdateTransactionRequest(data: UpdateTransactionPayload): UpdateTransactionRequest {
    return {
      ...(data.categorizationFeedback
        ? { categorizationFeedback: data.categorizationFeedback }
        : data.suggestionPredictionId
          ? {
              categorizationFeedback: {
                method: 'single_edit' as const,
                suggestions: [
                  {
                    transactionId: data.id,
                    predictionId: data.suggestionPredictionId,
                    rankedBucketIds: [...(data.suggestedBucketIds ?? [])],
                  },
                ],
              },
            }
          : {}),
      amount: data.amount,
      merchant: data.merchant,
      description: data.description,
      labelIds: data.labelIds,
      date: data.date,
      bucketId: data.bucketId,
      type: data.type as UpdateTransactionRequest['type'],
      children: data.children?.map(child => ({
        id: child.id,
        amount: child.amount,
        description: child.description,
        labelIds: child.labelIds,
        bucketId: child.bucketId,
      })),
    };
  }

  //TODO: Add more filters later
  matchesFilters(transaction: Transaction, filter: TransactionParams): boolean {
    const bucketIdFilter = !filter.bucketId || transaction.bucketId === filter.bucketId;
    const bucketIdsFilter =
      !filter.bucketIds || filter.bucketIds.length === 0 || filter.bucketIds.indexOf(transaction.bucketId) > -1;
    const labelIdsFilter =
      !filter.labelIds ||
      filter.labelIds.length === 0 ||
      filter.labelIds.some(labelId => transaction.labelIds?.includes(labelId));
    const unbucketedOnlyFilter = !filter.unbucketedOnly || transaction.bucketId == null;
    const deletedOnlyFilter = !filter.deletedOnly || transaction.deleted;
    const merchantFilter =
      !filter.merchant || transaction.merchant.toLowerCase().indexOf(filter.merchant.toLowerCase()) > -1;
    const monthYearFilter =
      !filter.monthYear || DateTime.fromISO(transaction.date).toFormat('yyyy-MM') === filter.monthYear;

    return (
      bucketIdFilter &&
      bucketIdsFilter &&
      labelIdsFilter &&
      unbucketedOnlyFilter &&
      deletedOnlyFilter &&
      merchantFilter &&
      monthYearFilter
    );
  }

  createFilterParams(filter: TransactionFilters): TransactionFilterParams {
    const params: TransactionFilterParams = {};
    if (filter.labelIds.length > 0) params.labelId = filter.labelIds;
    if (filter.deletedOnly) params.deletedOnly = true;
    if (filter.bucketIds.length > 0) params.bucketId = filter.bucketIds;
    if (filter.unbucketedOnly) params.unbucketedOnly = true;
    if (filter.merchant) params.merchant = filter.merchant;

    params.suggestedBucketIds = true; // TODO: in the backend we might want to limit this to only transactions that are not already sorted
    return params;
  }

  private transactionPageParamsFromFilter(
    filter: TransactionFilters,
    pageNumber: number,
    pageSize: number,
  ): TransactionParams {
    return {
      pageNumber,
      pageSize,
      bucketIds: filter.bucketIds,
      labelIds: filter.labelIds,
      ...(filter.deletedOnly ? { deletedOnly: true } : {}),
      ...(filter.unbucketedOnly ? { unbucketedOnly: true } : {}),
      ...(filter.merchant ? { merchant: filter.merchant } : {}),
      ...(filter.suggestedBucketIds ? { suggestedBucketIds: true } : {}),
    };
  }

  private invalidateAfterBulkMutation(transactions: Array<Pick<Transaction, 'bucketId' | 'date'>>): void {
    this.invalidateAfterTransactionMutation({
      bucketDetails: transactions,
      monthYears: transactions.map(transaction => this.monthYearFromTransactionDate(transaction.date)),
    });
  }

  private invalidateAfterTransactionMutation(impact: TransactionMutationCacheImpact): void {
    this.invalidateLoadedTransactionPages();
    void this.queryClient.invalidateQueries({ queryKey: userQueryKeys.session(), refetchType: 'none' });
    this.invalidateBucketDetailsForTransactions(impact.bucketDetails || []);

    const monthYears = Array.from(
      new Set((impact.monthYears || []).filter((monthYear): monthYear is string => !!monthYear)),
    );
    if (monthYears.length > 0) {
      this.groupService.invalidateGroupPages(monthYears);
      return;
    }

    this.groupService.invalidateCurrentGroupPage();
  }

  private monthYearFromTransactionDate(date?: string | null): string | null {
    if (!date) return null;
    return DateTime.fromISO(date).toFormat('yyyy-MM');
  }

  private invalidateBucketDetailsForTransactions(
    transactions: Array<Pick<Transaction, 'bucketId' | 'date'> | Pick<UpdateTransactionPayload, 'bucketId' | 'date'>>,
  ): void {
    transactions.forEach(transaction => {
      if (!transaction.bucketId) return;

      this.bucketService.invalidateBucketDetail(
        transaction.bucketId,
        this.monthYearFromTransactionDate(transaction.date),
      );
    });
  }

  private invalidateLoadedTransactionPages(): void {
    void this.queryClient.invalidateQueries({ queryKey: transactionQueryKeys.all });
  }

  private requestTransactionPage(
    key: string,
    filter: TransactionFilters,
    params: TransactionFilterParams & { pageSize: number },
  ): Promise<TransactionPage> {
    const inFlightRequest = this.inFlightTransactionRequests.get(key);
    if (inFlightRequest) return inFlightRequest;

    this.rememberTransactionPageFilter(key, filter);

    const request = this.queryClient
      .fetchQuery(this.buildTransactionPageQueryOptions(filter, params))
      .finally(() => this.inFlightTransactionRequests.delete(key));

    this.inFlightTransactionRequests.set(key, request);
    return request;
  }

  transactionPageQueryOptions(filter: TransactionFilters) {
    return this.buildTransactionPageQueryOptions(filter, {
      pageSize: PAGE_SIZE,
      ...this.createFilterParams(filter),
    });
  }

  unbudgetedTransactionsQueryOptions() {
    return this.transactionPageQueryOptions(UNBUDGETED_TRANSACTIONS_FILTER);
  }

  unbudgetedTransactionsWithSuggestionsQueryOptions() {
    return this.transactionPageQueryOptions(UNBUDGETED_TRANSACTIONS_WITH_SUGGESTIONS_FILTER);
  }

  deletedTransactionsQueryOptions() {
    return this.transactionPageQueryOptions(DELETED_TRANSACTIONS_FILTER);
  }

  private buildTransactionPageQueryOptions(
    filter: TransactionFilters,
    params: TransactionFilterParams & { pageSize: number },
  ) {
    const cachedPage = this.cachedTransactions(filter);

    return queryOptions({
      queryKey: transactionQueryKeys.page(this.transactionPageKey(filter)),
      queryFn: () =>
        this.fetchTransactionPageFromBackend(params).then(transactionPage => {
          this.persistTransactionPage(filter, transactionPage);
          return transactionPage;
        }),
      ...(cachedPage.data ? { initialData: cachedPage } : {}),
    });
  }

  transactionRequestQueryOptions(params: TransactionParams) {
    return queryOptions({
      queryKey: transactionQueryKeys.request(this.transactionParamsKey(params)),
      queryFn: () => this.fetchTransactionPageFromBackend(params),
    });
  }

  private fetchTransactionPageFromBackend(
    params: TransactionParams | (TransactionFilterParams & { pageSize: number }),
  ): Promise<TransactionPage> {
    return firstValueFrom(
      this.backend.request<TransactionPage & ApiTransactionPage>({
        type: 'get',
        apiRoute: 'transaction',
        params: this.transactionBackendQueryParams(params),
      }),
    );
  }

  private transactionBackendQueryParams(
    params: TransactionParams | (TransactionFilterParams & { pageSize: number }),
  ): BackendRequestParams {
    const queryParams: Record<string, unknown> = { ...params };

    if ('bucketIds' in params && params.bucketIds && params.bucketIds.length > 0)
      queryParams.bucketId = params.bucketIds;
    if ('labelIds' in params && params.labelIds && params.labelIds.length > 0) queryParams.labelId = params.labelIds;

    delete queryParams.bucketIds;
    delete queryParams.labelIds;
    return queryParams as BackendRequestParams;
  }

  private fetchBucketTransactionPageFromBackend(
    bucketId: string,
    monthYear: string,
    pageNumber: number,
    pageSize: number,
  ): Promise<TransactionPage> {
    return firstValueFrom(
      this.backend.request<Bucket>({
        type: 'get',
        apiRoute: `bucket/${bucketId}/${monthYear}`,
        params: { pageNumber, pageSize },
      }),
    ).then(bucket => bucket.transactions ?? this.emptyBucketTransactionPage(bucketId, monthYear, pageNumber, pageSize));
  }

  private emptyBucketTransactionPage(
    bucketId: string,
    monthYear: string,
    pageNumber: number,
    pageSize: number,
  ): TransactionPage {
    return {
      data: [],
      pageNumber,
      pageSize,
      totalRecords: 0,
      params: {
        bucketIds: [bucketId],
        monthYear,
        pageNumber,
        pageSize,
        suggestedBucketIds: true,
      },
      _objectType: 'transactionPage',
    };
  }

  private persistTransactionPage(filter: TransactionFilters, transactionPage: TransactionPage): void {
    if (this.isUnbudgetedTransactionsWithSuggestionsFilter(filter)) {
      this.setUnbudgetedTransactionsWithSuggestions(transactionPage);
      return;
    }

    if (this.isUnbudgetedTransactionsFilter(filter)) {
      this.setUnbudgetedTransactions(transactionPage);
      return;
    }

    if (this.isDeletedTransactionsFilter(filter)) {
      this.setDeletedransactions(transactionPage);
      return;
    }

    this.setTransactions(transactionPage);
  }

  private cachedUnbudgetedTransactions(): TransactionPage {
    const cachedQueryPage = this.queryClient.getQueryData<TransactionPage>(
      transactionQueryKeys.page(this.unbudgetedTransactionsKey()),
    );
    if (cachedQueryPage) return cachedQueryPage;
    return {} as TransactionPage;
  }

  private cachedUnbudgetedTransactionsWithSuggestions(): TransactionPage {
    const cachedQueryPage = this.queryClient.getQueryData<TransactionPage>(
      transactionQueryKeys.page(this.unbudgetedTransactionsWithSuggestionsKey()),
    );
    if (cachedQueryPage) return cachedQueryPage;
    return {} as TransactionPage;
  }

  private cachedDeletedTransactions(): TransactionPage {
    const cachedQueryPage = this.queryClient.getQueryData<TransactionPage>(
      transactionQueryKeys.page(this.deletedTransactionsKey()),
    );
    if (cachedQueryPage) return cachedQueryPage;
    return {} as TransactionPage;
  }

  private filterFromPage(page: TransactionPage): TransactionFilters {
    const filter: TransactionFilters = {
      labelIds: page.params.labelIds || [],
      bucketIds: page.params.bucketIds || [],
      deletedOnly: page.params.deletedOnly === true || page.params.deletedOnly === 'true',
      unbucketedOnly: page.params.unbucketedOnly === true || page.params.unbucketedOnly === 'true',
    };
    if (page.params.suggestedBucketIds === true || page.params.suggestedBucketIds === 'true')
      filter.suggestedBucketIds = true;
    if (page.params.merchant) filter.merchant = page.params.merchant;
    return filter;
  }

  private transactionPageKey(filter?: TransactionFilters): string {
    return JSON.stringify({
      bucketIds: [...(filter?.bucketIds || [])].sort(),
      deletedOnly: !!filter?.deletedOnly,
      labelIds: [...(filter?.labelIds || [])].sort(),
      merchant: filter?.merchant || '',
      suggestedBucketIds: !!filter?.suggestedBucketIds,
      unbucketedOnly: !!filter?.unbucketedOnly,
    });
  }

  private transactionParamsKey(params: TransactionParams): string {
    return JSON.stringify({
      ...params,
      bucketIds: [...(params.bucketIds || [])].sort(),
      labelIds: [...(params.labelIds || [])].sort(),
    });
  }

  private rememberTransactionPageFilter(key: string, filter: TransactionFilters): void {
    this.transactionPageFilters.set(key, filter);
  }

  private unbudgetedTransactionsKey(): string {
    return this.transactionPageKey(UNBUDGETED_TRANSACTIONS_FILTER);
  }

  private deletedTransactionsKey(): string {
    return this.transactionPageKey(DELETED_TRANSACTIONS_FILTER);
  }

  private unbudgetedTransactionsWithSuggestionsKey(): string {
    return this.transactionPageKey(UNBUDGETED_TRANSACTIONS_WITH_SUGGESTIONS_FILTER);
  }

  private isUnbudgetedTransactionsWithSuggestionsFilter(filter: TransactionFilters): boolean {
    return this.transactionPageKey(filter) === this.unbudgetedTransactionsWithSuggestionsKey();
  }

  private isUnbudgetedTransactionsFilter(filter: TransactionFilters): boolean {
    return this.transactionPageKey(filter) === this.unbudgetedTransactionsKey();
  }

  private isDeletedTransactionsFilter(filter: TransactionFilters): boolean {
    return this.transactionPageKey(filter) === this.deletedTransactionsKey();
  }

  private describeError(error: unknown): string {
    if (error instanceof Error) return error.message;
    return 'Unable to load transactions.';
  }
}
